import React, { ReactElement, useContext, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  Checkbox,
  Button,
  Paper,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Computer as ComputerIcon,
  ElectricBolt as ElectricBoltIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  Place as PlaceIcon,
  Construction as ConstructionIcon,
  ShoppingCart as ShoppingCartIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useTextDomainContext } from "@/app/providers";
import { usePageTitle } from "@/hooks";
import { useNavigate } from "react-router";
import { QtyCounter } from "./helpers";
import { PRICING_CONFIG, DKK_CONVERSION_RATE } from "./Services.constants";

export const Services = (): ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const theme = useTheme();
  const navigate = useNavigate();

  usePageTitle(gettext("Services"));

  // Currency & Billing Frequency States
  const [currency, setCurrency] = useState<"EUR" | "DKK">("EUR");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly",
  );

  // Quantity States
  const [setupChecked, setSetupChecked] = useState(true);
  const [energyElectricity, setEnergyElectricity] = useState(true);
  const [energyGas, setEnergyGas] = useState(true);
  const [usersQty, setUsersQty] = useState(1);
  const [companiesQty, setCompaniesQty] = useState(1);
  const [countriesQty, setCountriesQty] = useState(1);
  const [locationsQty, setLocationsQty] = useState(1);

  // Conversion rate computation
  const rate = currency === "DKK" ? DKK_CONVERSION_RATE : 1.0;

  // Format utility
  const formatPriceString = (val: number) => {
    const formatted = new Intl.NumberFormat(
      currency === "DKK" ? "da-DK" : "en-US",
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
    ).format(val);
    return currency === "EUR" ? `€${formatted}` : `${formatted} kr.`;
  };

  // Calculations (yearly is monthly * 12)
  const billingMultiplier = billingCycle === "yearly" ? 12 : 1;

  // 1. Setup (One-time)
  const setupSubtotalEur = setupChecked ? PRICING_CONFIG.setup.priceEur : 0;
  const setupSubtotal = setupSubtotalEur * rate;

  // 2. Platform License (Fixed qty 1)
  const platformQty = 1;
  const platformSubtotalEur =
    PRICING_CONFIG.platform.priceEur * platformQty * billingMultiplier;
  const platformSubtotal = platformSubtotalEur * rate;

  // 3. Energy Types (Max qty 2, calculated dynamically)
  const energyQty = (energyElectricity ? 1 : 0) + (energyGas ? 1 : 0);
  const energySubtotalEur =
    PRICING_CONFIG.energy.priceEur * energyQty * billingMultiplier;
  const energySubtotal = energySubtotalEur * rate;

  // 4. Users (Min qty 1)
  const usersSubtotalEur =
    PRICING_CONFIG.users.priceEur * usersQty * billingMultiplier;
  const usersSubtotal = usersSubtotalEur * rate;

  // 5. Companies / Subsidiaries (Min qty 1)
  const companiesSubtotalEur =
    PRICING_CONFIG.companies.priceEur * companiesQty * billingMultiplier;
  const companiesSubtotal = companiesSubtotalEur * rate;

  // 6. Countries (Min qty 1)
  const countriesSubtotalEur =
    PRICING_CONFIG.countries.priceEur * countriesQty * billingMultiplier;
  const countriesSubtotal = countriesSubtotalEur * rate;

  // 7. Locations / Meters (Min qty 1)
  const locationsSubtotalEur =
    PRICING_CONFIG.locations.priceEur * locationsQty * billingMultiplier;
  const locationsSubtotal = locationsSubtotalEur * rate;

  // Recurring Subtotal
  const totalRecurringEur =
    PRICING_CONFIG.platform.priceEur * platformQty +
    PRICING_CONFIG.energy.priceEur * energyQty +
    PRICING_CONFIG.users.priceEur * usersQty +
    PRICING_CONFIG.companies.priceEur * companiesQty +
    PRICING_CONFIG.countries.priceEur * countriesQty +
    PRICING_CONFIG.locations.priceEur * locationsQty;

  const totalRecurring = totalRecurringEur * rate * billingMultiplier;

  // Grand Total
  const grandTotal = setupSubtotal + totalRecurring;

  return (
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        py: { xs: 2, md: 3 },
      }}
    >
      {/* Header Banner */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 4,
          px: { xs: 3, md: 4 },
          py: { xs: 3, md: 4 },
          mb: 3,
          color: theme.palette.common.white,
          backgroundColor: theme.palette.primary.main,
          boxShadow: "0 24px 60px rgba(17, 37, 63, 0.22)",
        }}
      >
        <Stack spacing={2} sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={gettext("Flexible Subscriptions")}
              sx={{
                bgcolor: alpha("#FFFFFF", 0.16),
                color: "common.white",
                fontWeight: 600,
              }}
            />
            <Chip
              label={gettext("Scalable Pricing")}
              sx={{
                bgcolor: alpha("#FFFFFF", 0.16),
                color: "common.white",
                fontWeight: 600,
              }}
            />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {gettext("Services & Billing Calculator")}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 700 }}>
            {gettext(
              "Estimate and configure your tailored energy services package. Adjust quantities and select your preferred billing interval and currency.",
            )}
          </Typography>
        </Stack>
      </Box>

      {/* Configuration Toolbar */}
      <Card sx={{ mb: 4, p: 2 }}>
        <Grid
          container
          spacing={2}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                {gettext("Billing Cycle:")}
              </Typography>
              <ToggleButtonGroup
                value={billingCycle}
                exclusive
                onChange={(_, val) => val && setBillingCycle(val)}
                size="small"
                color="primary"
              >
                <ToggleButton value="monthly" sx={{ px: 2 }}>
                  {gettext("Monthly")}
                </ToggleButton>
                <ToggleButton value="yearly" sx={{ px: 2 }}>
                  {gettext("Yearly")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 6 }}
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                {gettext("Currency:")}
              </Typography>
              <ToggleButtonGroup
                value={currency}
                exclusive
                onChange={(_, val) => val && setCurrency(val)}
                size="small"
                color="primary"
              >
                <ToggleButton value="EUR" sx={{ px: 2 }}>
                  {gettext("EUR (€)")}
                </ToggleButton>
                <ToggleButton value="DKK" sx={{ px: 2 }}>
                  {gettext("DKK (kr.)")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Main Form Area */}
      <Grid container spacing={4}>
        {/* Left Column: Services list */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Stack spacing={2.5}>
            {/* 1. Custom Setup */}
            <Card
              sx={{
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: "flex-start", flex: 1, minWidth: 280 }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      color: theme.palette.secondary.main,
                      display: "flex",
                    }}
                  >
                    <ConstructionIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {gettext("Custom Setup & Implementation")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {gettext(
                        "One-time onboarding, system integration, custom branding, and workflow setup.",
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        px: 1,
                        py: 0.5,
                        bgcolor: "action.selected",
                        borderRadius: 1,
                      }}
                    >
                      {gettext("One-time:")}{" "}
                      {formatPriceString(PRICING_CONFIG.setup.priceEur * rate)}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ alignItems: "center" }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={setupChecked}
                        onChange={(e) => setSetupChecked(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      setupChecked ? gettext("Included") : gettext("Excluded")
                    }
                    sx={{ m: 0 }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, minWidth: 100, textAlign: "right" }}
                  >
                    {formatPriceString(setupSubtotal)}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* 2. Platform License */}
            <Card
              sx={{
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: "flex-start", flex: 1, minWidth: 280 }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      display: "flex",
                    }}
                  >
                    <ComputerIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {gettext("Platform License")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {gettext(
                        "Core license access to the platform console, basic dashboarding, and report generation.",
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        px: 1,
                        py: 0.5,
                        bgcolor: "action.selected",
                        borderRadius: 1,
                      }}
                    >
                      {formatPriceString(
                        PRICING_CONFIG.platform.priceEur * rate,
                      )}{" "}
                      / {gettext("month")}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ alignItems: "center" }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "text.secondary", pr: 2 }}
                  >
                    {gettext("1 (Fixed)")}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, minWidth: 100, textAlign: "right" }}
                  >
                    {formatPriceString(platformSubtotal)}
                    {billingCycle === "yearly"
                      ? gettext(" / yr")
                      : gettext(" / mo")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* 3. Energy Types */}
            <Card
              sx={{
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: "flex-start", flex: 1, minWidth: 280 }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      display: "flex",
                    }}
                  >
                    <ElectricBoltIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {gettext("Energy Types")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {gettext(
                        "Track consumption and carbon footprint for different energy mediums (Max 2).",
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        px: 1,
                        py: 0.5,
                        bgcolor: "action.selected",
                        borderRadius: 1,
                        display: "inline-block",
                        mb: 1.5,
                      }}
                    >
                      {formatPriceString(PRICING_CONFIG.energy.priceEur * rate)}{" "}
                      / {gettext("month per unit")}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={energyElectricity}
                            onChange={(e) =>
                              setEnergyElectricity(e.target.checked)
                            }
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {gettext("Electricity")}
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={energyGas}
                            onChange={(e) => setEnergyGas(e.target.checked)}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {gettext("Natural Gas")}
                          </Typography>
                        }
                      />
                    </Stack>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ alignItems: "center" }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      minWidth: 60,
                      textAlign: "center",
                      pr: 2,
                    }}
                  >
                    {gettext("Qty:")} {energyQty}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, minWidth: 100, textAlign: "right" }}
                  >
                    {formatPriceString(energySubtotal)}
                    {billingCycle === "yearly"
                      ? gettext(" / yr")
                      : gettext(" / mo")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* 4. Users */}
            <Card
              sx={{
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: "flex-start", flex: 1, minWidth: 280 }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      display: "flex",
                    }}
                  >
                    <PeopleIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {gettext("Users")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {gettext(
                        "Number of administrative and viewer users with portal access.",
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        px: 1,
                        py: 0.5,
                        bgcolor: "action.selected",
                        borderRadius: 1,
                      }}
                    >
                      {formatPriceString(PRICING_CONFIG.users.priceEur * rate)}{" "}
                      / {gettext("month per user")}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ alignItems: "center" }}
                >
                  <QtyCounter
                    val={usersQty}
                    min={PRICING_CONFIG.users.minQty}
                    onChange={setUsersQty}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, minWidth: 100, textAlign: "right" }}
                  >
                    {formatPriceString(usersSubtotal)}
                    {billingCycle === "yearly"
                      ? gettext(" / yr")
                      : gettext(" / mo")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* 5. Companies */}
            <Card
              sx={{
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: "flex-start", flex: 1, minWidth: 280 }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      display: "flex",
                    }}
                  >
                    <BusinessIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {gettext("Companies / Subsidiaries")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {gettext(
                        "Number of corporate entities or subsidiary accounts required under the tenant.",
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        px: 1,
                        py: 0.5,
                        bgcolor: "action.selected",
                        borderRadius: 1,
                      }}
                    >
                      {formatPriceString(
                        PRICING_CONFIG.companies.priceEur * rate,
                      )}{" "}
                      / {gettext("month per company")}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ alignItems: "center" }}
                >
                  <QtyCounter
                    val={companiesQty}
                    min={PRICING_CONFIG.companies.minQty}
                    onChange={setCompaniesQty}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, minWidth: 100, textAlign: "right" }}
                  >
                    {formatPriceString(companiesSubtotal)}
                    {billingCycle === "yearly"
                      ? gettext(" / yr")
                      : gettext(" / mo")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* 6. Countries */}
            <Card
              sx={{
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: "flex-start", flex: 1, minWidth: 280 }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      display: "flex",
                    }}
                  >
                    <PublicIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {gettext("Countries")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {gettext(
                        "Number of distinct countries in which energy metrics are analyzed.",
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        px: 1,
                        py: 0.5,
                        bgcolor: "action.selected",
                        borderRadius: 1,
                      }}
                    >
                      {formatPriceString(
                        PRICING_CONFIG.countries.priceEur * rate,
                      )}{" "}
                      / {gettext("month per country")}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ alignItems: "center" }}
                >
                  <QtyCounter
                    val={countriesQty}
                    min={PRICING_CONFIG.countries.minQty}
                    onChange={setCountriesQty}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, minWidth: 100, textAlign: "right" }}
                  >
                    {formatPriceString(countriesSubtotal)}
                    {billingCycle === "yearly"
                      ? gettext(" / yr")
                      : gettext(" / mo")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* 7. Locations */}
            <Card
              sx={{
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: "flex-start", flex: 1, minWidth: 280 }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      display: "flex",
                    }}
                  >
                    <PlaceIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {gettext("Locations / Meters")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {gettext(
                        "Number of unique delivery points, facilities, or physical meters monitored.",
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        px: 1,
                        py: 0.5,
                        bgcolor: "action.selected",
                        borderRadius: 1,
                      }}
                    >
                      {formatPriceString(
                        PRICING_CONFIG.locations.priceEur * rate,
                      )}{" "}
                      / {gettext("month per location")}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ alignItems: "center" }}
                >
                  <QtyCounter
                    val={locationsQty}
                    min={PRICING_CONFIG.locations.minQty}
                    onChange={setLocationsQty}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, minWidth: 100, textAlign: "right" }}
                  >
                    {formatPriceString(locationsSubtotal)}
                    {billingCycle === "yearly"
                      ? gettext(" / yr")
                      : gettext(" / mo")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Right Column: Order Summary Panel */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Box sx={{ position: "sticky", top: 24 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ShoppingCartIcon color="primary" /> {gettext("Order Summary")}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2} sx={{ mb: 3 }}>
                {setupChecked && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {gettext("Custom Setup (One-time)")}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatPriceString(setupSubtotal)}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    {gettext("Platform License (x1)")}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatPriceString(platformSubtotal)}
                  </Typography>
                </Box>

                {energyQty > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {gettext("Energy Types")} (x{energyQty})
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatPriceString(energySubtotal)}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    {gettext("Users")} (x{usersQty})
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatPriceString(usersSubtotal)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    {gettext("Companies")} (x{companiesQty})
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatPriceString(companiesSubtotal)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    {gettext("Countries")} (x{countriesQty})
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatPriceString(countriesSubtotal)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    {gettext("Meters / Locations")} (x{locationsQty})
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatPriceString(locationsSubtotal)}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {/* Recurring Subtotal */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  {billingCycle === "yearly"
                    ? gettext("Total Billed Annually")
                    : gettext("Total Billed Monthly")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: "primary.main" }}
                >
                  {formatPriceString(totalRecurring)}
                </Typography>
              </Box>

              {setupChecked && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {gettext("One-time Setup Fee")}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatPriceString(setupSubtotal)}
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{
                        textTransform: "uppercase",
                        fontWeight: 700,
                        fontSize: "0.7rem",
                      }}
                    >
                      {gettext("Grand Total")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {gettext("excl. VAT")}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 900, color: "text.primary" }}
                  >
                    {formatPriceString(grandTotal)}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => {
                  navigate("/user-account/checkout", {
                    state: {
                      setupChecked,
                      energyElectricity,
                      energyGas,
                      usersQty,
                      companiesQty,
                      countriesQty,
                      locationsQty,
                      currency,
                      billingCycle,
                      grandTotal,
                      setupSubtotal,
                      platformSubtotal,
                      energySubtotal,
                      usersSubtotal,
                      companiesSubtotal,
                      countriesSubtotal,
                      locationsSubtotal,
                      totalRecurring,
                    },
                  });
                }}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.35)}`,
                  },
                }}
              >
                {gettext("Proceed to Checkout")}
              </Button>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
